/**
 * AI generation failure reasons surfaced by StockBlocks (CNC-1383) and forwarded through the
 * AI Toolkit submit and `[taskId]` status routes as `failureReason`.
 *
 * Classification happens in StockBlocks — the shell only presents the reason and decides whether
 * retry is allowed. Shared by every AI Toolkit tool's error flow.
 */

export type FailureReason =
    | 'moderation'
    | 'aspect_ratio'
    | 'asset_too_large'
    | 'provider_error'
    | 'generation_failed'
    | 'timed_out'
    | 'source_unavailable'
    | 'unsupported_fps'
    | 'unsupported_input';

const FAILURE_REASONS: ReadonlySet<string> = new Set<FailureReason>([
    'moderation',
    'aspect_ratio',
    'asset_too_large',
    'provider_error',
    'generation_failed',
    'timed_out',
    'source_unavailable',
    'unsupported_fps',
    'unsupported_input',
]);

/** Narrows an untrusted server string to a known FailureReason (or null). */
export function toFailureReason(value: string | null | undefined): FailureReason | null {
    return value != null && FAILURE_REASONS.has(value) ? (value as FailureReason) : null;
}

// Known-cause failures are the user's to fix (change prompt / swap image / regenerate the source),
// so retry is blocked.
// A null reason means an unclassified condition, which stays retryable.
const NON_RETRYABLE_REASONS: ReadonlySet<FailureReason> = new Set<FailureReason>([
    'moderation',
    'aspect_ratio',
    'asset_too_large',
    'source_unavailable',
    'unsupported_fps',
    'unsupported_input',
]);

export function canRetry(reason: FailureReason | null | undefined): boolean {
    return reason == null || !NON_RETRYABLE_REASONS.has(reason);
}

// Reasons whose copy says nothing beyond "it failed" — a caller with better context about what was
// running should supply its own lead sentence instead.
const UNINFORMATIVE_REASONS: ReadonlySet<FailureReason> = new Set<FailureReason>([
    'provider_error',
    'generation_failed',
]);

export function hasSpecificCause(reason: FailureReason | null | undefined): boolean {
    return reason != null && !UNINFORMATIVE_REASONS.has(reason);
}

// Failures the user must fix by changing their input (prompt or image) → surfaced as 422.
// Mirrors StockBlocks' input-rejection list (AbstractAiGenerationController). Kept separate
// from NON_RETRYABLE_REASONS on purpose: a future reason could be non-retryable yet server-side
// (e.g. an account/provider ban), which should stay a 500.
const USER_FIXABLE_REJECTIONS: ReadonlySet<FailureReason> = new Set<FailureReason>([
    'moderation',
    'aspect_ratio',
    'asset_too_large',
    'source_unavailable',
    'unsupported_fps',
    'unsupported_input',
]);

export function isUserFixableRejection(reason: FailureReason | null | undefined): boolean {
    return reason != null && USER_FIXABLE_REJECTIONS.has(reason);
}
